import { Button, Form, Input, InputNumber, Switch } from 'antd';
import { useEffect, useState } from 'react';
import type { AppSettings } from '@/types/settings';
import { useTestSmtpMutation } from '@/modules/settings/settingsApi';
import styles from '../Settings.module.scss';

interface SmtpConfigTabProps {
  settings?: AppSettings;
  loading?: boolean;
  onSave: (values: AppSettings['smtp']) => Promise<void>;
  onNotify?: (type: 'success' | 'error', message: string) => void;
}

export function SmtpConfigTab({ settings, loading, onSave, onNotify }: SmtpConfigTabProps) {
  const [form] = Form.useForm<AppSettings['smtp']>();
  const [testSmtp, { isLoading: isTesting }] = useTestSmtpMutation();
  const [testTo, setTestTo] = useState('');

  useEffect(() => {
    if (settings?.smtp) {
      form.setFieldsValue({
        ...settings.smtp,
        password: '',
      });
      setTestTo(settings.smtp.fromEmail || settings.smtp.username || '');
    }
  }, [form, settings?.smtp]);

  async function handleTestSmtp() {
    try {
      const result = await testSmtp({ to: testTo.trim() || undefined }).unwrap();
      onNotify?.('success', result.message ?? 'Test email sent.');
    } catch (error) {
      let message = 'SMTP test failed.';
      if (error && typeof error === 'object' && 'data' in error) {
        const data = (error as { data?: { message?: string | string[] } }).data;
        if (typeof data?.message === 'string') message = data.message;
        else if (Array.isArray(data?.message)) message = data.message.join(' ');
      }
      onNotify?.('error', message);
    }
  }

  return (
    <div>
      <h2 className={styles.sectionTitle}>SMTP configuration</h2>
      <p className={styles.sectionDesc}>
        Configure outgoing email settings for order notifications, OTP messages, and customer emails.
      </p>

      <Form
        form={form}
        layout="vertical"
        disabled={loading || isTesting}
        onFinish={onSave}
        requiredMark={false}
      >
        <div className={styles.formGrid}>
          <Form.Item
            label="SMTP host"
            name="host"
            rules={[{ required: true, message: 'SMTP host is required.' }]}
          >
            <Input placeholder="smtp.gmail.com" />
          </Form.Item>

          <Form.Item
            label="SMTP port"
            name="port"
            rules={[{ required: true, message: 'SMTP port is required.' }]}
          >
            <InputNumber min={1} max={65535} style={{ width: '100%' }} placeholder="587" />
          </Form.Item>

          <Form.Item label="Username" name="username">
            <Input placeholder="noreply@betiyan.com" />
          </Form.Item>

          <Form.Item label="Password" name="password">
            <Input.Password placeholder="Enter SMTP password" autoComplete="new-password" />
            {settings?.smtp.hasPassword ? (
              <p className={styles.secretHint}>Leave blank to keep the existing password.</p>
            ) : null}
          </Form.Item>

          <Form.Item label="From email" name="fromEmail">
            <Input type="email" placeholder="noreply@betiyan.com" />
          </Form.Item>

          <Form.Item label="From name" name="fromName">
            <Input placeholder="indiastatpublications" />
          </Form.Item>
        </div>

        <div className={styles.switchRow}>
          <div className={styles.switchLabel}>
            <span className={styles.switchTitle}>Use SSL/TLS</span>
            <span className={styles.switchHint}>
              Turn on only for port 465. Port 587 uses STARTTLS automatically — leave this off.
            </span>
          </div>
          <Form.Item name="secure" valuePropName="checked" noStyle>
            <Switch />
          </Form.Item>
        </div>

        <div className={styles.actions}>
          <Button type="primary" htmlType="submit" loading={loading}>
            Save changes
          </Button>
        </div>
      </Form>

      <div className={styles.testSmtp}>
        <h3 className={styles.testSmtpTitle}>Send test email</h3>
        <p className={styles.sectionDesc}>
          Save SMTP settings first, then send a test message to confirm delivery.
        </p>
        <div className={styles.testSmtpRow}>
          <Input
            type="email"
            placeholder="Recipient email"
            value={testTo}
            onChange={(event) => setTestTo(event.target.value)}
            disabled={isTesting}
          />
          <Button type="default" loading={isTesting} onClick={handleTestSmtp}>
            Send test email
          </Button>
        </div>
      </div>
    </div>
  );
}
